import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import ReactGA from 'react-ga4';

import {
  IBookPackageWithCard,
  ICardBooking,
  IErrorField,
  ICarsDriver,
} from '@common-types';
import { ICarDetails, ISessionKey } from '@share/common-types';
import {
  VALIDATION_ERROR,
  EXTERNAL_ERROR,
  PRICE_CHANGED,
  SOLD_OUT,
  ERROR,
  SAVING_MISMATCH,
  ROOMS_UNAVAILABLE_ERROR,
  CHECK_OUT_PAYMENT_ERROR,
  INVALID_PAYLOAD,
  CHECKOUT_ERROR,
  STATUS_CONFIRMED
} from '@constants';
import { Urls, SESSION_KEY_LABEL, CARS_SESSION_KEY_LABEL, C_BOOKING_CONFIRMED } from '@share/constants';
import { getHeaders, axiosInstance, AppThunk, UrlUtils } from '@share/utils';
import { get, isString } from 'lodash';
import { scrollTop } from '@share/utils';
import { carsActions, getAccountCreditLine, getUserCards, getUserWallet, getUserWalletReward } from '@share/store/slices';
import { setCarsDetails } from './cars-details';
import { StripeChargeStatus } from '../../share/utils/stripe';
import { confirmPaymentWithEmbeddedNextActions, isStripeExpressAuthorized, isStripeExpressPending, type StripeDirectConfirm } from '../../share/utils/stripe-confirm-payment';
import { buildStripeExpressReturnUrl } from '../../share/utils/stripe-return-url';
import { Stripe, StripeElements } from '@stripe/stripe-js';

export interface ICarsReviewBookState {
  loading: boolean;
  error: string;
  expiredSession: boolean;
  loadingBooking: boolean;
  errorBooking: string;
  bookingComplete: boolean;
  booking: ICarDetails | null | undefined;
  card: ICardBooking;
  stripe: any;
  driver: ICarsDriver;
  errorsField: IErrorField[] | null | undefined;
  isExternalError: boolean;
  isPriceChangedError: boolean;
  isSoldOutError: boolean;
  errorsBookingMessage: IErrorField[] | null | undefined;
  isBookingInProgress: boolean;
  isSavingsMismatch: boolean;
  threeDSModalVisible: boolean;
  threeDSUrl: string | null | undefined;
  threeDSId: string | null | undefined;
  threeDSLoading: boolean | null | undefined;
  selectedCarsReviewClientCash: number | null | undefined;
  selectedCarsReviewClientCashStr: string | null | undefined;
  errorStripe: string;
  stripeLoading: boolean;
  rewardId: number | null;
}

const initialCarDState: any = {
  addressLine: '',
  cvv: '',
  cardNumber: '',
  cardType: null,
  city: '',
  country: 'US',
  state: '',
  expireDate: '',
  holderName: '',
  phone: '',
  zipCode: '',
  addPaymentMethod: false
};

const initialStripeState: any = {
  addressLine: '',
  city: '',
  country: 'US',
  state: '',
  zipCode: ''
};

const initialState: ICarsReviewBookState = {
  loading: false,
  error: '',
  expiredSession: false,
  loadingBooking: false,
  errorBooking: '',
  bookingComplete: false,
  booking: null,
  card: { ...initialCarDState },
  stripe: { ...initialStripeState },
  driver: {
    namePrefix: '',
    givenName: '',
    surname: '',
    country: 'US',
    phone: '',
    email: '',
    birthDate: '',
  },
  errorsField: null,
  isExternalError: false,
  isPriceChangedError: false,
  isSoldOutError: false,
  errorsBookingMessage: null,
  isBookingInProgress: false,
  isSavingsMismatch: false,
  threeDSModalVisible: false,
  threeDSUrl: null,
  threeDSId: null,
  threeDSLoading: null,
  selectedCarsReviewClientCash: null,
  selectedCarsReviewClientCashStr: '',
  errorStripe: '',
  stripeLoading: false,
  rewardId: null,
};

const zeroItem = 0;

const carsReviewBookSlice = createSlice({
  name: 'cars-review-book',
  initialState,
  reducers: {
    setCarsLoadingBook: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setCarsErrorBook: (state: ICarsReviewBookState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setCarsExpiredSession: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.expiredSession = payload;
    },
    setCarsSavingsMismatch: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isSavingsMismatch = payload;
    },
    setCarsThreeDSModalVisible: (
      state: ICarsReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.threeDSModalVisible = payload;
    },
    setCarsThreeDSUrl: (state: ICarsReviewBookState, { payload }: PayloadAction<string | null | undefined>) => {
      state.threeDSUrl = payload;
    },
    setCarsThreeDSId: (state: ICarsReviewBookState, { payload }: PayloadAction<string | null | undefined>) => {
      state.threeDSId = payload;
    },
    setCarsThreeDSLoading: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean | null | undefined>) => {
      state.threeDSLoading = payload;
    },
    setCarsLoadingBooking: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.loadingBooking = payload;
    },
    setCarsErrorBooking: (state: ICarsReviewBookState, { payload }: PayloadAction<string>) => {
      state.errorBooking = payload;
    },
    setCarsErrorStripe: (state: ICarsReviewBookState, { payload }: PayloadAction<string>) => {
      state.errorStripe = payload;
    },
    setCarsBookingComplete: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.bookingComplete = payload;
    },
    setCarsBooking: (state: ICarsReviewBookState, { payload }: PayloadAction<ICarDetails | null | undefined>) => {
      state.booking = payload;
    },
    setCarsCard: (state: ICarsReviewBookState, { payload }: PayloadAction<ICardBooking>) => {
      state.card = payload;
    },
    setCarsStripe: (state: ICarsReviewBookState, { payload }: PayloadAction<ICardBooking>) => {
      state.stripe = payload;
    },
    setCarsDriver: (state: ICarsReviewBookState, { payload }: PayloadAction<ICarsDriver>) => {
      state.driver = payload;
    },
    setCarsErrorsField: (
      state: ICarsReviewBookState,
      { payload }: PayloadAction<IErrorField[] | null | undefined>,
    ) => {
      state.errorsField = payload;
    },
    setCarsExternalError: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isExternalError = payload;
    },
    setCarsPriceChangedError: (
      state: ICarsReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isPriceChangedError = payload;
    },
    setCarsSoldOutError: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isSoldOutError = payload;
    },
    setCarsErrorsBookingMessage: (
      state: ICarsReviewBookState,
      { payload }: PayloadAction<IErrorField[] | null | undefined>,
    ) => {
      state.errorsBookingMessage = payload;
    },
    setCarsBookingInProgress: (
      state: ICarsReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isBookingInProgress = payload;
    },
    setSelectedCarsReviewClientCash: (
      state: ICarsReviewBookState,
      { payload }: PayloadAction<number | null | undefined>,
    ) => {
      state.selectedCarsReviewClientCash = payload;
    },
    setSelectedCarsReviewClientCashStr: (
      state: ICarsReviewBookState,
      { payload }: PayloadAction<string | null | undefined>,
    ) => {
      state.selectedCarsReviewClientCashStr = payload;
    },
    setCarsReviewRewardId: (
      state: ICarsReviewBookState,
      { payload }: PayloadAction<number | null>,
    ) => {
      state.rewardId = payload;
    },

    resetCarsState: (state: ICarsReviewBookState) => {
      return { ...initialState, selectedCarsReviewClientCash: state.selectedCarsReviewClientCash };
    },
    setStripeLoading: (state: ICarsReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.stripeLoading = payload;
    },
  },
});

export const {
  setCarsLoadingBook,
  setCarsErrorBook,
  setCarsExpiredSession,
  setCarsLoadingBooking,
  setCarsErrorBooking,
  setCarsBookingComplete,
  setCarsBooking,
  setCarsCard,
  setCarsDriver,
  setCarsErrorsField,
  setCarsExternalError,
  setCarsPriceChangedError,
  setCarsSoldOutError,
  setCarsErrorsBookingMessage,
  setCarsBookingInProgress,
  resetCarsState,
  setCarsSavingsMismatch,
  setCarsThreeDSModalVisible,
  setCarsThreeDSUrl,
  setCarsThreeDSId,
  setCarsThreeDSLoading,
  setSelectedCarsReviewClientCash,
  setSelectedCarsReviewClientCashStr,
  setCarsReviewRewardId,
  setCarsErrorStripe,
  setCarsStripe,
  setStripeLoading,
} = carsReviewBookSlice.actions;

export const carsReviewBookReducer = carsReviewBookSlice.reducer;

export const resetCreditCard = (): AppThunk => {
  return async (dispatch) => {
    dispatch(setCarsCard({ ...initialCarDState }));
  }
}

export const getCarsBookingStripe =
  (
    bookingGuid: string,
    sessionKey: ISessionKey,
    paymentMethod: string | undefined,
    isCancel: boolean = false
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setStripeLoading(true));
    dispatch(setCarsLoadingBooking(true));

    // Build query params (mirrors hotels: pass the real sessionId so the BFF can resolve the booking/customer)
    const params = {
      bookingId: bookingGuid,
      isCancel,
      sessionId: sessionKey?.value,
      paymentDescripcion: paymentMethod,
    };

    try {
      // Single GET call with params
      const { data } = await axiosInstance.get(Urls.CarsResumeExpress, {
        ...getHeaders(),
        params,
      });

      dispatch(handleProcessBookingComplete(data));

    } catch (err: any) {
      // Unified error message extraction
      const errorObject = isString(err) ? JSON.parse(err) : err;
      const data = errorObject.response?.data;
      const dataObject = data && isString(data) ? JSON.parse(data) : data;
      const message = dataObject?.reason ?? errorObject.message ??  "Unknown error";

      dispatch(setCarsErrorStripe(message));
      dispatch(handleErrorBookingComplete(err));
    } finally {
      // Tear down loading indicators
      dispatch(setCarsLoadingBooking(false));
      dispatch(setStripeLoading(false));
    }
  };

export const confirmPayment = (
  clientSecret: string,
  stripe: Stripe | undefined,
  elements: StripeElements | undefined,
  bookingGui: string,
  paymentMethod: string | undefined,
  /** Set for Afterpay/Clearpay and Cash App Pay, which are confirmed through their own Stripe API
   *  rather than through Elements (no ExpressCheckoutElement button exists for them). Carries the
   *  `payment_method_types` token and the billing details Stripe needs to build the PaymentMethod.
   *  Undefined for card and the wallets. */
  directConfirm?: StripeDirectConfirm,
): AppThunk => {
  return async (dispatch, getState) => {
    if (!stripe) {
      return;
    }
    const values = UrlUtils.getValues();
    const sessionKey =
      (values[CARS_SESSION_KEY_LABEL] as ISessionKey) ??
      ({ value: '', isInvalid: false, expireDate: '' } as ISessionKey);
    const accountUsername = getState().loginStore?.account?.name;
    const returnUrl = buildStripeExpressReturnUrl({
      accountUsername,
      vertical: 'cars',
      bookingGuid: bookingGui,
      sessionId: sessionKey?.value ?? '',
      paymentMethod: paymentMethod as string,
      fallbackAbsoluteUrl: `${import.meta.env.VITE_API_BFF_URL}${Urls.CarsResumeExpress}?bookingId=${bookingGui}`,
    });
    try {
      const { paymentIntent, error } = await confirmPaymentWithEmbeddedNextActions(stripe, {
        elements,
        clientSecret,
        returnUrl,
        paymentMethodType: directConfirm?.paymentMethodType,
        billingDetails: directConfirm?.billingDetails,
      });

      // Authorized (normally `requires_capture` under manual capture): proceed to resume, which books
      // with the provider and then captures (or voids on failure). See `isStripeExpressAuthorized`
      // for the statuses that count — redirect / QR methods (Klarna, Afterpay, Cash App Pay) can also
      // land on `processing`, and treating that as a failure would void a good authorization.
      if (isStripeExpressAuthorized(paymentIntent?.status)) {
        dispatch(getCarsBookingStripe(bookingGui, sessionKey, paymentMethod));
        dispatch(setCarsErrorStripe(''));
      } else if (isStripeExpressPending(paymentIntent?.status)) {
        // Customer dismissed the action without finishing it — closing Cash App Pay's QR modal is the
        // case this exists for. The authorization is still live, so do NOT resume: cancelling here
        // would void a payment the customer may be approving on their phone, and the BFF rejects it
        // with a 400 anyway (TRAV23-4969). Just clear the spinners and let them choose again.
        dispatch(setCarsErrorStripe(''));
        dispatch(setCarsLoadingBooking(false));
        dispatch(setStripeLoading(false));
      } else {
        // Anything else means the payment did not authorize. A hard
        // decline surfaces an `error`; a buyer cancellation (e.g. Amazon Pay BuyerCanceled)
        // resolves with no `error` and the PI in `requires_payment_method`. Both must resume
        // with isCancel=true so the pending express booking is voided and the UI recovers.
        dispatch(getCarsBookingStripe(bookingGui, sessionKey, paymentMethod, true));
        dispatch(
          setCarsErrorStripe(
            error?.message ??
              (paymentIntent as any)?.last_payment_error?.message ??
              'Payment was not completed. Please try again.',
          ),
        );
      }
    } catch (error: any) {
      if (typeof error === 'string') {
        dispatch(setCarsErrorStripe(error));
      }
      else {
        const err = error.message ? error.message : error?.response.data;
        dispatch(handleErrorBookingComplete(error));
        dispatch(setCarsErrorStripe(err));
      }
      dispatch(getCarsBookingStripe(bookingGui, sessionKey, paymentMethod, true));
    }
  };
};


export const getCarsBookPackageWithCard = (
  bookWithCard: IBookPackageWithCard,
  stripe?: Stripe,
  elements?: StripeElements,
  paymentMethod?: string | undefined,
  /** Forwarded to {@link confirmPayment}: set only for Afterpay/Clearpay and Cash App Pay. */
  directConfirm?: StripeDirectConfirm,
): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(setCarsLoadingBooking(true));
    dispatch(setCarsExpiredSession(false));
    dispatch(setCarsThreeDSModalVisible(false));
    dispatch(setCarsThreeDSUrl(null));
    dispatch(setCarsThreeDSId(null));
    dispatch(setCarsThreeDSLoading(false));

    const { rewardId } = getState().carsReviewBookStore;
    const postData: IBookPackageWithCard = { ...bookWithCard };
    if (rewardId != null) {
      postData.rewardId = rewardId;
    }

    try {
      const res = await axiosInstance.post(Urls.CarsCheckout, postData, {
        ...getHeaders(),
      });

      const bookingResponse = res?.data;

      //Stripe payment flow
      if (bookingResponse?.chargeStatus === StripeChargeStatus.PENDING_EXPRESS) {
        dispatch(
          confirmPayment(bookingResponse.clientSecret, stripe, elements, bookingResponse?.id, paymentMethod, directConfirm),
        );
        dispatch(setCarsThreeDSId(bookingResponse?.id));
      }

      if (bookingResponse?.chargeStatus === 'Pending3DS') {
        dispatch(setCarsLoadingBooking(false));
        dispatch(setCarsThreeDSModalVisible(true));
        dispatch(setCarsThreeDSId(bookingResponse?.id));
        dispatch(setCarsThreeDSUrl(bookingResponse?.urlRedirectTo3DS));
        return;
      }

      //Normal flow (card)
      if (bookingResponse?.chargeStatus !== StripeChargeStatus.PENDING_EXPRESS)
        dispatch(handleProcessBookingComplete(bookingResponse));
    } catch (error) {
      dispatch(handleErrorBookingComplete(error));
    } finally {
      scrollTop();
    }
  };
};

export const getCarsBookPackageWithCard3DS = (sessionKey: ISessionKey): AppThunk => {
  return async (dispatch, getState) => {
    const { carsReviewBookStore } = getState();
    const { threeDSId } = carsReviewBookStore;

    dispatch(setCarsThreeDSModalVisible(false));
    dispatch(setCarsThreeDSLoading(true));

    try {
      const res = await axiosInstance.post(Urls.CarsCheckoutResume, { bookingId: threeDSId, sessionKey }, { ...getHeaders() });
      dispatch(handleProcessBookingComplete(res?.data));

      dispatch(setCarsThreeDSUrl(null));
      dispatch(setCarsThreeDSId(null));
      dispatch(setCarsThreeDSLoading(false));

    } catch (error) {
      dispatch(handleErrorBookingComplete(error));
    } finally {
      dispatch(setCarsThreeDSModalVisible(false));
      dispatch(setCarsThreeDSUrl(null));
      dispatch(setCarsThreeDSId(null));
      dispatch(setCarsThreeDSLoading(false));

      scrollTop();
    }
  };
};

const handleProcessBookingComplete = (data: any): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(setCarsLoadingBooking(true));

    const { navigationMenuStore, loginStore, carsDetailsStore } = getState();

    const status = data?.bookingStatusEnum;
    if (status === SOLD_OUT) {
      dispatch(setCarsErrorBook(ROOMS_UNAVAILABLE_ERROR));
      dispatch(setCarsExternalError(true));
    }

    if (status === STATUS_CONFIRMED) {
      dispatch(setCarsBookingComplete(true));

      // Stripe express resume returns the same ICarDetails shape as /cars/recheck. When the page was
      // reached via the Stripe return (no recheck ran), seed carsDetailsStore.car so the booking/details UI renders.
      if (!carsDetailsStore?.car) {
        dispatch(setCarsDetails(data));
      }

      const { account, userWallet } = loginStore;

      if (navigationMenuStore?.items?.promo) {
        UrlUtils.removeFromUrl(SESSION_KEY_LABEL);
      }

      dispatch(resetCreditCard());
      dispatch(setSelectedCarsReviewClientCash(null));
      dispatch(setSelectedCarsReviewClientCashStr(''));
      dispatch(carsActions.setSelectedCarsSearchClientCash(null));
      dispatch(getUserWallet(userWallet));
      dispatch(getUserWalletReward(userWallet));
      dispatch(getAccountCreditLine());
      dispatch(getUserCards());
      dispatch(setCarsCard({ ...initialCarDState }));

      ReactGA.event({
        category: account.name,
        action: `${C_BOOKING_CONFIRMED}_${account.name.toUpperCase()}`,
        label: `Booking confirmed on book`,
        nonInteraction: false,
      });

    } else {
      dispatch(setCarsBookingInProgress(true));
    }

    dispatch(setCarsLoadingBooking(false));

    dispatch(setCarsBooking(data));
  }
}

// A classified payment decline arrives with the readable, translated detail in errors[],
// while reason carries only the prefix. Hotels never needs this because the BFF joins the
// two before the response leaves, but a cars checkout is re-emitted verbatim, so we join
// them here to read identically. Same separator the BFF uses.
// When errors[] is absent or empty we keep showing reason exactly as before.
const withErrorDetail = (errorObject: any, errorReason: string): string => {
  const details = (get(errorObject, 'errors', []) || [])
    .map((item: any) => get(item, 'message', ''))
    .filter((message: string) => !!message);

  return details.length ? `${errorReason}: ${details.join(', ')}` : errorReason;
};

const handleErrorBookingComplete = (error: any): AppThunk => {
  return async (dispatch) => {

    const errorData = error?.response?.data;

    if (errorData) {
      // TODO to check
      const errorType = get(errorData, `[${zeroItem}].errorType`);
      const errorCode = get(errorData, `[${zeroItem}].errorCode`);

      if (errorType === VALIDATION_ERROR) {
        dispatch(setCarsErrorsField(error.response.data));
      } else if (errorType === EXTERNAL_ERROR) {
        dispatch(setCarsExternalError(true));
      } else if (errorCode === PRICE_CHANGED) {
        dispatch(setCarsPriceChangedError(true));
      } else if (errorCode === SOLD_OUT) {
        dispatch(setCarsSoldOutError(true));
      } else if (errorType === ERROR) {
        dispatch(setCarsErrorsBookingMessage(error.response.data));
      } else if (errorCode === SAVING_MISMATCH) {
        dispatch(setCarsExpiredSession(true));
        dispatch(setCarsSavingsMismatch(true));
      } else {
        const errorObject = isString(errorData) ? JSON.parse(errorData) : errorData;
        const errorMessage = get(errorObject, 'code', null);
        const errorReason = get(errorObject, 'reason', error.toString());

        const errorReasonInsufficientZbucks = errorReason === 'Insufficient Zbucks';
        const errorReasonSessionKeyNotValid = errorReason === 'SessionKey not valid';

        const externalErrors = [ROOMS_UNAVAILABLE_ERROR];
        const paymentErrors = [CHECK_OUT_PAYMENT_ERROR];
        const errors = [INVALID_PAYLOAD, CHECKOUT_ERROR];
        if (externalErrors.includes(errorMessage)) {
          dispatch(setCarsErrorBook(errorMessage));
          dispatch(setCarsExternalError(true));
        } else if (errorReasonSessionKeyNotValid) {
          dispatch(setCarsErrorBook('INSUFFICIENT_ZBUCKS_ERROR'));
          dispatch(setCarsExternalError(true));
        } else if (paymentErrors.includes(errorMessage)) {
          dispatch(setCarsErrorBooking(withErrorDetail(errorObject, errorReason)));
        } else if (errors.includes(errorMessage)) {
          dispatch(setCarsErrorBooking(errorReason));
        } else {
          dispatch(setCarsErrorBooking(errorMessage ? `error.code.${errorReasonInsufficientZbucks ? 'INSUFFICIENT_ZBUCKS_ERROR' : errorMessage}` : errorReason));
        }
      }

    } else {
      dispatch(setCarsErrorBooking(error.toString()));
    }
    dispatch(setCarsLoadingBooking(false));
  }
}